import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { getCurrentUserID } from '../../../api/currentUserID.js';
import { validateExtractionForSap } from '../../export/sap-validation.js';
import { getMailProcessing } from '../../information-extraction/api/getMailProcessing.js';
import { MailExtractionResult } from '../../information-extraction/db/MailExtractionResult.js';
import { syncRequestNotifications } from '../server/syncRequestNotifications.js';

/**
 * Bring a request's questions back in line after it was corrected by hand in the review UI.
 *
 * Without this the only way a question ever closed was answering it *through the question* —
 * so someone who simply fixed the position in the table left its notification standing, and
 * the register filled up with work that was already done.
 *
 * It re-persists `sapValidation` on the way, which the manual correction path never did
 * either: the status chip in the request list reads that field, and it was stale from the
 * moment a position was corrected until the next full extraction run.
 */
export const syncRequestQuestions = registerMutation({
  type: () => Boolean,
  operationName: 'syncRequestQuestions',
  resolve: (mailID: string) => {
    return async () => {
      if (!getCurrentUserID()) throw new NotLoggedInError();

      const request = await getMailProcessing(mailID)({
        _id: 1,
        mailID: 1,
        extractionResult: MailExtractionResult.allFields,
      });
      const extraction = request?.extractionResult;
      if (!request || !extraction) return false;

      const report = validateExtractionForSap(extraction);
      request.sapValidation = report;
      request.validatedAt = new Date();
      await request.save();

      await syncRequestNotifications({ mailID, result: extraction, report });
      return true;
    };
  },
  params: [{ name: 'mailID', type: () => String }],
});
